import {
	ActionBar,
	AddIcon,
	Button,
	CollapsibleSection,
	Input,
	Select,
	SpinnerWithBackground,
	Text,
	TextArea,
} from '@viapeople/components'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import type { EmailVariable } from '../../../hooks/useEmailTemplateEditor'
import { useEmailTemplateEditor } from '../../../hooks/useEmailTemplateEditor'
import { sessionStore } from '../../../sharedStores/sessionStore'
import { useToast } from '../../../utils/ToastContext'
import { useLoopStore } from '../store'
import type { LoopEmailTemplateTabInput } from './types'

const LOOP_VARIABLES: EmailVariable[] = [
	{
		key: '{{loops.name}}',
		label: 'Loop Name',
		description: 'The name of the loop',
		example: 'Weekly Team Check-in',
	},
	{
		key: '{{loops.description}}',
		label: 'Loop Description',
		description: 'The description of the loop',
		example: 'Share your weekly progress and feedback',
	},
	{
		key: '{{loops.responseLink}}',
		label: 'Response Link (Magic Link)',
		description: 'Direct link for participants to submit their response',
		example: 'https://app.example.com/loops/response/abc123',
		required: true,
	},
	{
		key: '{{loops.textQuestionLabel}}',
		label: 'Text Question Label',
		description: 'The label for the text question',
		example: 'What went well this week?',
	},
	{
		key: '{{loops.ratingQuestionLabel}}',
		label: 'Rating Question Label',
		description: 'The label for the rating question',
		example: 'How satisfied are you?',
	},
	{
		key: '{{loops.ratingScale}}',
		label: 'Rating Scale',
		description: 'The rating scale (5 or 10)',
		example: '5',
	},
	{
		key: '{{loops.launchedAt}}',
		label: 'Launch Date',
		description: 'When this iteration was launched',
		example: 'January 10, 2025',
	},
	{
		key: '{{user.firstName}}',
		label: 'User First Name',
		description: "The participant's first name",
		example: 'John',
	},
	{
		key: '{{user.lastName}}',
		label: 'User Last Name',
		description: "The participant's last name",
		example: 'Doe',
	},
]

export const LoopEmailTemplateTab = () => {
	const { loopId } = useParams<{ loopId: string }>()
	const { user } = sessionStore()
	const organizationId = user?.organizationId || ''
	const navigate = useNavigate()
	const toast = useToast()
	const { loop, useEditLoop } = useLoopStore()
	const { editLoop, isLoading: isEditing } = useEditLoop(loop?.id)

	const {
		handleSubmit,
		control,
		watch,
		formState: { errors },
		reset,
	} = useForm<LoopEmailTemplateTabInput>({
		defaultValues: {
			emailTemplateId: loop?.emailTemplateId || null,
		},
	})

	const selectedTemplateId = watch('emailTemplateId')

	const {
		isTemplateEditorOpen,
		editingTemplateId,
		templateForm,
		setTemplateForm,
		templateErrors,
		selectedTemplate,
		isLoadingTemplates,
		isLoadingTemplate,
		templateOptions,
		openCreateEditor,
		openEditEditor,
		closeTemplateEditor,
		handleSaveTemplate,
		handleCopyVariable,
		getPreview,
		isTemplateSaving,
	} = useEmailTemplateEditor({
		tag: 'loops',
		organizationId,
		variables: LOOP_VARIABLES,
		selectedTemplateId,
		onTemplateCreated: (id) => reset({ emailTemplateId: id }),
	})

	useEffect(() => {
		if (loop) {
			reset({
				emailTemplateId: loop.emailTemplateId || null,
			})
		}
	}, [loop, reset])

	const handleSubmitInternal = async (data: LoopEmailTemplateTabInput) => {
		if (!loop?.id) {
			toast.showToast('Please create the loop first (General tab)', 'error')
			return
		}

		if (data.emailTemplateId && selectedTemplate) {
			const hasResponseLink =
				selectedTemplate.subject?.toLowerCase().includes('{{loops.responselink}}') ||
				selectedTemplate.body?.toLowerCase().includes('{{loops.responselink}}')
			if (!hasResponseLink) {
				toast.showToast('Email template must include {{loops.responseLink}} variable', 'error')
				return
			}
		}

		try {
			await editLoop({
				emailTemplateId: data.emailTemplateId,
			})
		} catch (err) {
			if (err instanceof Error && !(err as any).data?.code) {
				toast.showToast(err.message, 'error')
			}
		}
	}

	const isPending = isEditing || isLoadingTemplate
	const preview = getPreview()

	return (
		<form onSubmit={handleSubmit(handleSubmitInternal)} className='relative pb-12 mt-6 w-full'>
			{isPending && <SpinnerWithBackground />}
			<div className='grid grid-cols-1 gap-8 px-6 max-w-6xl'>
				<div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
					<Text variant='small' weight='semibold' className='text-blue-900'>
						Loop Email
					</Text>
					<Text variant='xsmall' className='text-blue-700 mt-2'>
						This email is sent to all participants when a loop iteration is launched. It should include instructions and
						a link for participants to submit their response.
					</Text>
				</div>

				{/* Template Selection */}
				<div className='space-y-4'>
					<Controller
						name='emailTemplateId'
						control={control}
						render={({ field }) => (
							<Select
								label='Select Email Template (Optional)'
								placeholder='Choose an email template'
								options={templateOptions}
								value={field.value || ''}
								onChange={field.onChange}
								variant={errors.emailTemplateId ? 'error' : 'default'}
								hint='Select a template or leave empty to skip email notifications'
								disabled={isLoadingTemplates || isTemplateEditorOpen}
							/>
						)}
					/>

					{!isTemplateEditorOpen && (
						<div className='flex items-center gap-2'>
							{selectedTemplateId && (
								<Button variant='secondary' size='small' onClick={openEditEditor}>
									Edit Template
								</Button>
							)}
							<Button variant='secondary' size='small' icon={AddIcon} onClick={openCreateEditor}>
								Create New Template
							</Button>
						</div>
					)}
				</div>

				{/* Inline Template Editor */}
				{isTemplateEditorOpen && (
					<div className='border border-neutral-200 rounded-lg overflow-hidden'>
						<div className='bg-neutral-50 px-4 py-3 border-b border-neutral-200'>
							<Text variant='small' weight='semibold'>
								{editingTemplateId ? 'Edit Email Template' : 'Create Email Template'}
							</Text>
						</div>
						<div className='p-4 space-y-4 bg-white'>
							<Input
								label='Template Name'
								value={templateForm.name}
								placeholder='Enter template name'
								onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
								hint={templateErrors.name}
								variant={templateErrors.name ? 'error' : 'default'}
								disabled={isTemplateSaving}
							/>
							<Input
								label='Subject'
								value={templateForm.subject}
								placeholder='Enter email subject'
								onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
								hint={templateErrors.subject}
								variant={templateErrors.subject ? 'error' : 'default'}
								disabled={isTemplateSaving}
							/>
							<TextArea
								label='Body'
								value={templateForm.body}
								placeholder='Enter email body content. Use variables like {{loops.responseLink}} to personalize.'
								onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
								hint={templateErrors.body}
								variant={templateErrors.body ? 'error' : 'default'}
								disabled={isTemplateSaving}
								rows={10}
							/>
							<div className='flex justify-end gap-2 pt-2'>
								<Button variant='secondary' size='small' onClick={closeTemplateEditor} disabled={isTemplateSaving}>
									Cancel
								</Button>
								<Button variant='primary' size='small' onClick={handleSaveTemplate} isLoading={isTemplateSaving}>
									{editingTemplateId ? 'Update Template' : 'Create Template'}
								</Button>
							</div>
						</div>
					</div>
				)}

				{/* Available Variables */}
				<CollapsibleSection title='Available Email Variables' defaultOpen={!isTemplateEditorOpen}>
					<div className='space-y-3'>
						<Text variant='xsmall' className='text-gray-600'>
							Click on a variable to copy it to your clipboard. Use these in your email template to personalize
							messages.
						</Text>
						<div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
							{LOOP_VARIABLES.map((variable) => (
								<button
									key={variable.key}
									type='button'
									onClick={() => handleCopyVariable(variable.key)}
									className='flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left'>
									<div className='flex-1'>
										<div className='flex items-center gap-2'>
											<Text variant='xsmall' weight='semibold' className='font-mono'>
												{variable.key}
											</Text>
											{variable.required && (
												<span className='px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded'>Required</span>
											)}
										</div>
										<Text variant='xsmall' className='text-gray-600 mt-1'>
											{variable.description}
										</Text>
										<Text variant='xsmall' className='text-gray-500 mt-1 italic'>
											Example: {variable.example}
										</Text>
									</div>
								</button>
							))}
						</div>
					</div>
				</CollapsibleSection>

				{/* Template Preview */}
				{preview && (
					<div className='space-y-4'>
						<Text variant='small' weight='semibold'>
							Email Preview
						</Text>
						<div className='border rounded-lg overflow-hidden'>
							<div className='bg-gray-100 p-4 border-b'>
								<Text variant='xsmall' className='text-gray-600'>
									Subject
								</Text>
								<Text variant='small' weight='semibold' className='mt-1'>
									{preview.subject || '(No subject)'}
								</Text>
							</div>
							<div className='p-6 bg-white'>
								<div className='prose prose-sm max-w-none whitespace-pre-wrap'>{preview.body || '(Empty body)'}</div>
							</div>
						</div>
						<Text variant='xsmall' className='text-gray-600 italic'>
							This preview shows example data. Actual emails will use real participant data.
						</Text>
					</div>
				)}
			</div>
			<ActionBar
				rightActions={[
					{
						variant: 'secondary' as const,
						label: 'Back',
						onClick: () => navigate(`/loops/${loopId}/schedule`),
					},
					{
						variant: 'primary' as const,
						label: 'Save & Continue',
						onClick: handleSubmit(handleSubmitInternal),
					},
				]}
			/>
		</form>
	)
}
